<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses, appClass ]"
  >

    <component
      :is="layout"
      class="main-layout"
      :style="{paddingTop: `${offset}px` }"
    >
      <router-view />
    </component>

  </div>
</template>

<script>

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { ref, watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'
import LocalizationService from '@/services/localization-service'
import $http from '@/libs/axios'
import router from '@/router'

import LayoutVertical from '@/layouts/vertical/LayoutVertical.vue'
import LayoutFull from '@/layouts/full/LayoutFull.vue'

export default {
  provide() {
    const config = {}

    Object.defineProperty(config, 'isDark', {
      enumerable: true,
      get: () => this.isDark,
    })

    Object.defineProperty(config, 'isRTL', {
      enumerable: true,
      get: () => this.isRTL,
    })

    return {
      config,
      getTranslatedInfo: LocalizationService.getTranslate,
    }
  },
  components: {
    LayoutVertical,
    LayoutFull,

  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    isAuthenticated() {
      return this.$store.getters['auth/isAuthenticated']
    },
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    offset() {
      return this.subscriptionStatusHeight + this.verificationAlertHeight
    },
    appClass() {
      if (this.subscriptionStatusHeight && this.verificationAlertHeight) return 'verification-subscription-status-visible'
      if (this.subscriptionStatusHeight || this.verificationAlertHeight) return 'subscription-status-visible'
      return ''
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    const subscriptionStatusHeight = ref(0)
    const verificationAlertHeight = ref(0)

    LocalizationService.initLocale()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      subscriptionStatusHeight,
      verificationAlertHeight,
    }
  },
  mounted() {
    this.flutterMessageHandler = event => {
      const { action, token } = event.detail || {}
      if (action === 'flutter_logout') {
        store.dispatch('auth/LOGOUT').then(() => {
          router.push({ name: 'login' })
        })
        return
      }
      if (action === 'fcm_token' && token) {
        const sendFcmToken = () => {
          if (store.getters['auth/isAuthenticated']) {
            $http.post('user/fcm-token', {
              fcm_token: token,
              device_info: 'Flutter WebView',
            }).then(() => {
              store.commit('auth/SET_FCM_TOKEN_SENT', true)
            }).catch(() => {})
          }
        }
        sendFcmToken()
        // Retry once after 2s in case auth was still restoring (e.g. CHECK_TOKEN)
        if (!store.getters['auth/isAuthenticated']) {
          setTimeout(sendFcmToken, 2000)
        }
      }
    }
    window.addEventListener('flutter_message', this.flutterMessageHandler)
  },
  beforeDestroy() {
    if (this.flutterMessageHandler) {
      window.removeEventListener('flutter_message', this.flutterMessageHandler)
    }
  },
}
</script>

<style lang="scss">
#app.my-menu-full-screen .main-layout {
  padding-top: 0!important;
}
</style>
